import { type FC, type ReactNode, useState } from 'react';
import type { BaseTabProps, PersonalMeetingTab } from '../../types';
import { TabNavigation } from '../shared/TabNavigation';
import LobbyRoomTab from './LobbyRoomTab';
import MeetingDetailsTab from './MeetingDetailsTab';
import SecurityOptionsTab from './SecurityOptionsTab';

const tabComponents: Record<PersonalMeetingTab, FC<BaseTabProps>> = {
  'meeting-details': MeetingDetailsTab,
  'lobby-room': LobbyRoomTab,
  'security-options': SecurityOptionsTab,
};

interface MeetingTabsProps extends BaseTabProps {
  /** Controlled active tab (optional - uses internal state if not provided) */
  activeTab?: PersonalMeetingTab;
  /** Callback when tab changes (required when using controlled activeTab) */
  onTabChange?: (tab: PersonalMeetingTab) => void;
  /** Rendered right of the tabs. Holds Delete while the main pane is wide enough for it. */
  tabRowAction?: ReactNode;
}

const MeetingTabs: FC<MeetingTabsProps> = ({
  selectedMeeting,
  selectedMeetingData,
  isUpdating,
  updateMeetingData,
  toggleDefaultMeeting,
  isHostControlMode,
  activeTab: controlledActiveTab,
  onTabChange,
  tabRowAction,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<PersonalMeetingTab>('meeting-details');

  const activeTab = controlledActiveTab ?? internalActiveTab;
  const handleTabChange = onTabChange ?? setInternalActiveTab;

  const ActiveTabComponent = tabComponents[activeTab];

  const tabProps: BaseTabProps = {
    selectedMeeting,
    selectedMeetingData,
    isUpdating,
    updateMeetingData,
    toggleDefaultMeeting,
    isHostControlMode,
  };

  return (
    <>
      <div className="personal-meetings-modal__tab-row">
        <TabNavigation activeTab={activeTab} onChange={handleTabChange} />
        {tabRowAction && <div className="personal-meetings-modal__tab-actions">{tabRowAction}</div>}
      </div>
      <div className="personal-meetings-modal__tab-wrapper">
        {/* Without the key, drafts, edit mode and pending validation leak into the next selected meeting. */}
        <ActiveTabComponent key={selectedMeeting.room} {...tabProps} />
      </div>
    </>
  );
};

export default MeetingTabs;
